import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, startOfDay, endOfDay } from 'date-fns';
import { getCustomerCodes, getDeliveredShipments, CustomerOption, getDeliveredAlerts, OrgLevelKey } from '../api/shipments';
import { useOrgFilter, writeOrgSelection } from '../hooks/useOrgFilter';
import { DataTable } from 'components/ui/data-table';
import { deliveredColumns } from './columns';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';

interface DateRange {
  from?: Date;
  to?: Date;
}

interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

interface SortingState {
  id: string;
  desc: boolean;
}

interface AlertFilter {
  pod_not_received?: string;
  pending_invoice?: string;
  cust_id?: string;
}

interface AlertBucket {
  label: string;
  count: number;
  filters: AlertFilter;
}

interface AlertCard {
  id: string;
  title: string;
  type: 'bucketed' | 'single';
  style: 'positive' | 'negative' | 'neutral' | 'total';
  count?: number;
  filters?: AlertFilter;
  buckets?: AlertBucket[];
}

// Shown until the first /delivered/alerts response (same titles, labels and colours as
// the cards the API returns), with the numbers as loading placeholders
const PLACEHOLDER_ALERT_CARDS: AlertCard[] = [
  {
    id: 'pod_not_received', title: 'POD Not Received', type: 'bucketed', style: 'negative', count: 0,
    buckets: ['< 10 days', '10–20 days', '> 20 days'].map((label) => ({ label, count: 0, filters: {} })),
  },
  {
    // The API sends style "warning" for this card
    id: 'pending_invoice', title: 'Pending Invoice', type: 'bucketed', style: 'warning' as AlertCard['style'], count: 0,
    buckets: ['< 5 days', '5–10 days', '> 10 days'].map((label) => ({ label, count: 0, filters: {} })),
  },
];

const Delivered = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [sorting, setSorting] = useState<SortingState[]>([{ id: 'cn_date', desc: true }]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({ from: undefined, to: undefined });

  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [selectedCustId, setSelectedCustId] = useState<string | undefined>(undefined);

  // Org hierarchy filter (Division / Zone / Cluster / Controlling Branch / Branch-Project)
  const orgFilter = useOrgFilter('delivered');
  const { orgParams } = orgFilter;
  // Org selection of the last customer-list load, to spot org changes
  const lastCustomerOrgKey = useRef<string | null>(null);
  // Org selection of the last alerts load: card numbers show placeholders on first load and org changes
  const lastAlertsOrgKey = useRef<string | null>(null);

  const [alertCards, setAlertCards] = useState<AlertCard[]>(PLACEHOLDER_ALERT_CARDS);
  const [isLoadingAlerts, setIsLoadingAlerts] = useState(true);

  const [activeAlertFilters, setActiveAlertFilters] = useState<AlertFilter>({});

  // Set breadcrumbs
  useEffect(() => {
    setBreadcrumbs([
      { label: 'Shipments', href: '#' },
      { label: 'Delivered' }
    ]);
  }, [setBreadcrumbs]);

  useEffect(() => {
    const next = new URLSearchParams();

    next.set('page', String(pagination.pageIndex + 1));
    next.set('per_page', String(pagination.pageSize));

    if (sorting.length > 0) {
      next.set('sort_by', sorting[0].id);
      next.set('sort_order', sorting[0].desc ? 'desc' : 'asc');
    }

    if (globalFilter) next.set('search', globalFilter);
    if (selectedCustId) next.set('customer', selectedCustId);
    writeOrgSelection(next, orgParams);
    if (dateRange.from) next.set('from_date', format(dateRange.from, 'yyyy-MM-dd'));
    if (dateRange.to) next.set('to_date', format(dateRange.to, 'yyyy-MM-dd'));

    // 🟢 Add your active alert filters
    if (activeAlertFilters.pod_not_received) next.set('pod_not_received', activeAlertFilters.pod_not_received);
    if (activeAlertFilters.pending_invoice) next.set('pending_invoice', activeAlertFilters.pending_invoice);

    setSearchParams(next, { replace: true });
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
    globalFilter,
    selectedCustId,
    orgParams,
    dateRange,
    activeAlertFilters,
    setSearchParams
  ]);

  const fetchCustomerData = useCallback(async () => {
      const orgKey = JSON.stringify(orgParams);
      const orgChanged = lastCustomerOrgKey.current !== null && lastCustomerOrgKey.current !== orgKey;
      lastCustomerOrgKey.current = orgKey;
      try {
        const params: any = {};
        
        if (globalFilter) {
          params.search = globalFilter;
        }
        if (dateRange.from) {
          params.from = format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss');
        }
        if (dateRange.to) {
          params.to = format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss');
        }

        params.status = "delivered";
        Object.assign(params, orgParams);
  
        const response = await getCustomerCodes(params);
  
        if (response && response.length > 0) {
          const options: CustomerOption[] = response
            .filter(item => item?.cust_code)
            .map(item => ({
              cust_code: item.cust_code,
              cust_name: item.cust_name || item.cust_code,
            }))
            .sort((a, b) => a.cust_name.localeCompare(b.cust_name));

          setCustomerOptions(options);
          // After an org change, drop a selected customer that has no CNs under the new org
          if (orgChanged) {
            setSelectedCustId((prev) => (prev && !options.some((o) => o.cust_code === prev) ? undefined : prev));
          }
        } else {
          setCustomerOptions([]);
          if (orgChanged) setSelectedCustId(undefined);
        }
  
      } catch (error) {
        console.error("Error fetching customer data for dropdown:", error);
        setCustomerOptions([]);
      }
    }, [globalFilter, dateRange, orgParams]);

    const fetchAlerts = useCallback(async () => {
      const orgKey = JSON.stringify(orgParams);
      if (lastAlertsOrgKey.current !== orgKey) setIsLoadingAlerts(true);
      lastAlertsOrgKey.current = orgKey;
      try {
        const params: any = { ...orgParams };
        if (globalFilter) params.search = globalFilter;
        if (dateRange.from) params.from = format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss');
        if (dateRange.to) params.to = format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss');
        if (selectedCustId) params.cust_id = selectedCustId;

        const response = await getDeliveredAlerts(params);
        setAlertCards(response.cards || []);
      } catch (error) {
        console.error('Error fetching delivered alerts:', error);
        setAlertCards([]);
      } finally {
        setIsLoadingAlerts(false);
      }
    }, [globalFilter, dateRange, selectedCustId, orgParams]);

  const fetchShipments = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.pageIndex + 1,
        per_page: pagination.pageSize,
        search: globalFilter,
        sort_by: sorting[0]?.id || 'cn_date',
        sort_order: sorting[0]?.desc ? 'desc' : 'asc',
        ...orgParams,
      };

      if (dateRange.from) {
        params.from = format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss');
      }
      if (dateRange.to) {
        params.to = format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss');
      }

      if (selectedCustId) {
        params.cust_id = selectedCustId;
      }

      if(activeAlertFilters.pod_not_received) {
        params.pod_not_received = activeAlertFilters.pod_not_received;
      }

      if(activeAlertFilters.pending_invoice) {
        params.pending_invoice = activeAlertFilters.pending_invoice;
      }

      const response = await getDeliveredShipments(params);
      
      setShipments(response.items || []);
      setTotalCount(response.total_items || 0);
      setPageCount(response.total_pages || 0);
      setError("");
    } catch (error: any) {
      console.error('Error fetching delivered shipments:', error);
      setShipments([]);
      setTotalCount(0);
      setError(error?.response?.data?.detail || error?.message || "Failed to fetch shipments");
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize, globalFilter, sorting, dateRange, selectedCustId, orgParams, activeAlertFilters]);

  // Debounced search effect
  useEffect(() => {
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }
    
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, pageIndex: 0 }));
    }, 300);
    
    setSearchDebounceTimer(timer);
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [globalFilter]);

    useEffect(() => {
      fetchCustomerData();
    }, [fetchCustomerData]);


    useEffect(() => {
      fetchAlerts();
    }, [fetchAlerts]); 

  // Fetch shipments when pagination or sorting changes (including after search reset)
  useEffect(() => {
    fetchShipments();
  }, [fetchShipments]);

  const handlePaginationChange = (updater: any) => {
    setPagination(updater);
  };

  const handleSortingChange = (updater: any) => {
    setSorting(updater);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleGlobalFilterChange = (value: string) => {
    setGlobalFilter(value);
  };

  const handleDateRangeChange = (newDateRange: DateRange) => {
    setDateRange(newDateRange || { from: undefined, to: undefined });
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleCustomerChange = (code: string | undefined) => {
    setSelectedCustId(code);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleOrgLevelChange = (key: OrgLevelKey, value: string | undefined) => {
    orgFilter.setLevel(key, value);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleAlertFilterChange = (filters: AlertFilter) => {
    setActiveAlertFilters(filters);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  return (
    <DataTable 
      columns={deliveredColumns} 
      data={shipments}
      loading={loading}
      error={error}
      pagination={pagination}
      onPaginationChange={handlePaginationChange}
      sorting={sorting}
      onSortingChange={handleSortingChange}
      globalFilter={globalFilter}
      onGlobalFilterChange={handleGlobalFilterChange}
      dateRange={dateRange}
      onDateRangeChange={handleDateRangeChange}
      totalCount={totalCount}
      pageCount={pageCount}
      customerOptions={customerOptions}
      selectedCustId={selectedCustId}
      onCustomerChange={handleCustomerChange}
      orgFilter={{ ...orgFilter, setLevel: handleOrgLevelChange }}
      alertCards={alertCards}
      activeAlertFilters={activeAlertFilters}
      onAlertFilterChange={handleAlertFilterChange}
      isLoadingAlerts={isLoadingAlerts}
    />
  );
};

export default Delivered;
